import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, CardBody, SimpleGrid, Badge, HStack, VStack, Text, IconButton, Button,
  useDisclosure, useToast, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalBody, ModalFooter, ModalCloseButton, FormControl, FormLabel, Input,
  Textarea, Switch, NumberInput, NumberInputField, Heading, Box, Tag, Wrap, WrapItem,
  Checkbox, Select, Image, Spinner, Center,
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon, StarIcon } from '@chakra-ui/icons';
import { FiCamera } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import PageShell from '../components/PageShell';
import ConfirmModal from '../components/ConfirmModal';
import roomTypeService from '../services/roomTypeService';
import roomTypeImageService from '../services/roomTypeImageService';
import taxRateService from '../services/taxRateService';

// Image URLs come back as /api/booking-engine/... paths on local dev —
// absolutise against the API origin (same pattern as CustomFields.js).
const API_ORIGIN = (() => {
  try {
    return new URL(process.env.REACT_APP_API_URL || 'http://localhost:8000/api').origin;
  } catch {
    return '';
  }
})();
const absUrl = (u) => (u && u.startsWith('/') ? `${API_ORIGIN}${u}` : (u || ''));

// What one unit of this room type is sold as — display-only (no pricing/
// availability effect). Dorm/hostel inventory (e.g. a Triple sold as 3
// shared beds) sets this to 'bed' so the widget/admin read "beds" instead
// of "rooms". Keep in sync with backend_appdesko.com rooms/models.py
// RoomType.UNIT_LABEL_CHOICES.
const UNIT_LABEL_OPTIONS = ['room', 'bed', 'tent', 'apartment', 'villa', 'cabin'];

const emptyForm = {
  code: '', name: '', description: '', baseRate: '', capacityAdults: 2, capacityChildren: 0,
  maximumOccupancy: 2,
  unitLabel: 'room',
  // Extra-guest surcharge fields (feature added 2026-07). Included =
  // per-room allowance in base rate. Extra = per-night, per-guest
  // surcharge above the included threshold. Defaults keep it a
  // no-op — only kicks in when the operator sets non-zero prices.
  includedAdults: 2, includedChildren: 0,
  extraAdultPrice: 0, extraChildPrice: 0,
  // Taxes: taxRateIds = attached hotel TaxRates (their SUM is the rate
  // bookings use). taxRate = legacy flat % — only applies when no taxes
  // are attached.
  taxRateIds: [], taxRate: 0,
  isActive: true, isSmokingAllowed: false, isPetFriendly: false,
};

const RoomTypeManagement = () => {
  const { t } = useTranslation();
  const [roomTypes, setRoomTypes] = useState([]);
  const [hotelTaxRates, setHotelTaxRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const deleteDisclosure = useDisclosure();
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await roomTypeService.getAll();
      setRoomTypes(data?.results || data || []);
    } catch { setError(t('roomTypes.errors.loadFailed')); }
    finally { setLoading(false); }
  }, [t]);

  const loadTaxRates = useCallback(async () => {
    try {
      const data = await taxRateService.getAll();
      const rates = data?.results || data || [];
      setHotelTaxRates(rates.filter(r => r.is_active !== false));
    } catch { /* tax attach UI degrades to hidden; room types still load */ }
  }, []);

  useEffect(() => { load(); loadTaxRates(); }, [load, loadTaxRates]);

  const openCreate = () => { setForm(emptyForm); setEditingId(null); onOpen(); };

  const openEdit = (rt) => {
    setForm({
      code: rt.code, name: rt.name, description: rt.description || '', baseRate: rt.baseRate,
      capacityAdults: rt.capacityAdults, capacityChildren: rt.capacityChildren,
      maximumOccupancy: rt.maximumOccupancy,
      unitLabel: rt.unitLabel || 'room',
      // Fall back to sensible defaults for rooms saved before the
      // extra-guest fields existed (backend still returns them, but
      // legacy fixtures could be missing).
      includedAdults: rt.includedAdults ?? 2,
      includedChildren: rt.includedChildren ?? 0,
      extraAdultPrice: rt.extraAdultPrice ?? 0,
      extraChildPrice: rt.extraChildPrice ?? 0,
      taxRateIds: (rt.taxRates || []).map(tr => tr.id),
      taxRate: rt.taxRate ?? 0,
      isActive: rt.isActive, isSmokingAllowed: rt.isSmokingAllowed, isPetFriendly: rt.isPetFriendly,
    });
    setEditingId(rt.id);
    onOpen();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Adapter expects `taxRates` as a list of TaxRate ids.
      const payload = { ...form, taxRates: form.taxRateIds, taxRate: Number(form.taxRate) || 0 };
      delete payload.taxRateIds;
      if (editingId) await roomTypeService.update(editingId, payload);
      else await roomTypeService.create(payload);
      toast({ title: editingId ? t('roomTypes.toasts.updated') : t('roomTypes.toasts.created'), status: 'success', duration: 2000 });
      onClose(); load();
    } catch (err) {
      toast({ title: t('common.error'), description: JSON.stringify(err.response?.data), status: 'error', duration: 4000 });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await roomTypeService.remove(deleteTarget.id);
      toast({ title: t('roomTypes.toasts.deleted'), status: 'success', duration: 2000 });
      deleteDisclosure.onClose(); load();
    } catch (err) {
      toast({ title: t('roomTypes.toasts.cannotDelete'), description: err.response?.status === 409 ? t('roomTypes.toasts.roomsExist') : t('common.error'), status: 'error', duration: 3000 });
    }
  };

  const set = (field) => (e) => setForm({ ...form, [field]: e.target?.value ?? e });
  const setNum = (field) => (_, val) => setForm({ ...form, [field]: val });

  // --- Room-type photos (what the booking widget shows) ---
  const [photosTarget, setPhotosTarget] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoDeleteTarget, setPhotoDeleteTarget] = useState(null);
  const fileInputRef = useRef(null);

  const loadPhotos = useCallback(async (roomTypeId) => {
    try {
      setPhotosLoading(true);
      const data = await roomTypeImageService.list(roomTypeId);
      setPhotos(data?.images || []);
    } catch {
      toast({ title: t('roomTypes.photos.loadFailed'), status: 'error', duration: 3000 });
    } finally { setPhotosLoading(false); }
  }, [t, toast]);

  const openPhotos = (rt) => { setPhotosTarget(rt); setPhotos([]); loadPhotos(rt.id); };
  const closePhotos = () => { setPhotosTarget(null); load(); }; // refresh card photo counts

  const handlePhotoFiles = async (e) => {
    const files = e.target.files;
    if (!files?.length || !photosTarget) return;
    try {
      setUploading(true);
      await roomTypeImageService.upload(photosTarget.id, files);
      toast({ title: t('roomTypes.photos.uploaded'), status: 'success', duration: 2000 });
      loadPhotos(photosTarget.id);
    } catch (err) {
      toast({
        title: t('roomTypes.photos.uploadFailed'),
        description: err.response?.data?.detail || '',
        status: 'error', duration: 5000,
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSetCover = async (img) => {
    try {
      await roomTypeImageService.setCover(photosTarget.id, img.id);
      toast({ title: t('roomTypes.photos.coverSet'), status: 'success', duration: 2000 });
      loadPhotos(photosTarget.id);
    } catch { toast({ title: t('common.error'), status: 'error', duration: 3000 }); }
  };

  const handleDeletePhoto = async () => {
    try {
      await roomTypeImageService.remove(photosTarget.id, photoDeleteTarget.id);
      toast({ title: t('roomTypes.photos.deleted'), status: 'success', duration: 2000 });
      setPhotoDeleteTarget(null);
      loadPhotos(photosTarget.id);
    } catch { toast({ title: t('common.error'), status: 'error', duration: 3000 }); }
  };

  const toggleTaxRate = (id) => setForm((f) => ({
    ...f,
    taxRateIds: f.taxRateIds.includes(id)
      ? f.taxRateIds.filter(x => x !== id)
      : [...f.taxRateIds, id],
  }));

  const selectedTaxTotal = hotelTaxRates
    .filter(r => form.taxRateIds.includes(r.id))
    .reduce((sum, r) => sum + Number(r.rate || 0), 0);

  return (
    <PageShell
      title={t('roomTypes.title')}
      subtitle={t('roomTypes.subtitle', { count: roomTypes.length })}
      breadcrumbs={[{ label: t('roomTypes.title') }]}
      loading={loading}
      error={error}
      actions={<Button leftIcon={<AddIcon />} colorScheme="blue" size="sm" onClick={openCreate}>{t('roomTypes.buttons.add')}</Button>}
    >
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
        {roomTypes.map(rt => (
          <Card key={rt.id} shadow="sm" borderWidth="1px" position="relative">
            <CardBody>
              <HStack justify="space-between" mb={2}>
                <VStack align="start" spacing={0}>
                  <HStack>
                    <Heading size="sm">{rt.name}</Heading>
                    <Badge colorScheme="gray" fontSize="2xs">{rt.code}</Badge>
                  </HStack>
                  <Text fontSize="xs" color="gray.500">{rt.description}</Text>
                </VStack>
                <Badge colorScheme={rt.isActive ? 'green' : 'red'} fontSize="2xs">
                  {rt.isActive ? t('roomTypes.status.active') : t('roomTypes.status.inactive')}
                </Badge>
              </HStack>

              <SimpleGrid columns={2} spacing={2} my={3}>
                <Box>
                  <Text fontSize="xs" color="gray.400">{t('roomTypes.fields.baseRate')}</Text>
                  <Text fontSize="sm" fontWeight="600">{rt.baseRate}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.400">{t('roomTypes.fields.maxOccupancy')}</Text>
                  <Text fontSize="sm">{rt.maximumOccupancy}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.400">{t('roomTypes.card.adultsChildren')}</Text>
                  <Text fontSize="sm">{rt.capacityAdults} / {rt.capacityChildren}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.400">{t('roomTypes.card.rooms')}</Text>
                  <Text fontSize="sm">{rt.totalRoomsCount ?? '-'}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.400">{t('roomTypes.card.soldAs')}</Text>
                  <Text fontSize="sm">{t(`roomTypes.unitLabels.${rt.unitLabel || 'room'}`)}</Text>
                </Box>
              </SimpleGrid>

              <Wrap spacing={1} mb={3}>
                {rt.isPetFriendly && <WrapItem><Tag size="sm" colorScheme="green">{t('roomTypes.fields.petFriendly')}</Tag></WrapItem>}
                {rt.isSmokingAllowed && <WrapItem><Tag size="sm" colorScheme="orange">{t('roomTypes.fields.smoking')}</Tag></WrapItem>}
                {/* Extra-guest surcharge badge — surfaces which room
                    types have opted in without opening the edit form. */}
                {(Number(rt.extraAdultPrice) > 0 || Number(rt.extraChildPrice) > 0) && (
                  <WrapItem>
                    <Tag size="sm" colorScheme="purple" title={t('roomTypes.card.extrasTitle')}>
                      {t('roomTypes.card.extras', { adult: Number(rt.extraAdultPrice || 0), child: Number(rt.extraChildPrice || 0) })}
                    </Tag>
                  </WrapItem>
                )}
                {(rt.taxRates || []).map(tr => (
                  <WrapItem key={`tax-${tr.id}`}>
                    <Tag size="sm" colorScheme="teal" title={t('roomTypes.card.attachedTax')}>{tr.name} {Number(tr.rate)}%</Tag>
                  </WrapItem>
                ))}
                {(rt.taxRates || []).length === 0 && Number(rt.taxRate) > 0 && (
                  <WrapItem>
                    <Tag size="sm" colorScheme="teal" variant="outline" title={t('roomTypes.card.legacyTax')}>
                      {t('roomTypes.card.legacyTaxTag', { rate: Number(rt.taxRate) })}
                    </Tag>
                  </WrapItem>
                )}
                {(rt.amenities || []).slice(0, 4).map(a => (
                  <WrapItem key={a.id}><Tag size="sm" variant="outline">{a.name}</Tag></WrapItem>
                ))}
              </Wrap>

              <HStack justify="space-between" spacing={1}>
                {/* Photo nudge — the widget shows a gray placeholder
                    until the hotel uploads at least one photo. */}
                {Number(rt.imagesCount) > 0 ? (
                  <Tag size="sm" variant="subtle" colorScheme="blue" cursor="pointer" onClick={() => openPhotos(rt)}>
                    {t('roomTypes.photos.countTag', { count: rt.imagesCount })}
                  </Tag>
                ) : (
                  <Tag size="sm" variant="subtle" colorScheme="orange" cursor="pointer" onClick={() => openPhotos(rt)}>
                    {t('roomTypes.photos.noneTag')}
                  </Tag>
                )}
                <HStack spacing={1}>
                  <IconButton size="xs" icon={<FiCamera />} variant="ghost" onClick={() => openPhotos(rt)} aria-label={t('roomTypes.photos.button')} />
                  <IconButton size="xs" icon={<EditIcon />} variant="ghost" onClick={() => openEdit(rt)} aria-label={t('common.edit')} />
                  <IconButton size="xs" icon={<DeleteIcon />} variant="ghost" colorScheme="red"
                    onClick={() => { setDeleteTarget(rt); deleteDisclosure.onOpen(); }} aria-label={t('common.delete')} />
                </HStack>
              </HStack>
            </CardBody>
          </Card>
        ))}
      </SimpleGrid>

      {/* Create/Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingId ? t('roomTypes.modal.editTitle') : t('roomTypes.modal.createTitle')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <HStack w="100%" spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">{t('roomTypes.fields.code')}</FormLabel>
                  <Input size="sm" value={form.code} onChange={set('code')} placeholder="STD" maxLength={10} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">{t('roomTypes.fields.name')}</FormLabel>
                  <Input size="sm" value={form.name} onChange={set('name')} placeholder={t('roomTypes.fields.namePlaceholder')} />
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel fontSize="sm">{t('roomTypes.fields.description')}</FormLabel>
                <Textarea size="sm" value={form.description} onChange={set('description')} rows={2} />
              </FormControl>
              <HStack w="100%" spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">{t('roomTypes.fields.baseRate')}</FormLabel>
                  <Input size="sm" type="number" value={form.baseRate} onChange={set('baseRate')} />
                </FormControl>
              </HStack>
              <HStack w="100%" spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">{t('roomTypes.fields.adultsCapacity')}</FormLabel>
                  <Input size="sm" type="number" value={form.capacityAdults} onChange={set('capacityAdults')} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('roomTypes.fields.childrenCapacity')}</FormLabel>
                  <Input size="sm" type="number" value={form.capacityChildren} onChange={set('capacityChildren')} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('roomTypes.fields.maxOccupancy')}</FormLabel>
                  <Input size="sm" type="number" value={form.maximumOccupancy} onChange={set('maximumOccupancy')} />
                </FormControl>
              </HStack>

              {/* Sold as — display-only label for the booking widget and
                  admin ("3 beds available" instead of "3 rooms available"
                  for dorm/hostel inventory). No pricing/availability effect. */}
              <FormControl w="100%">
                <FormLabel fontSize="sm">{t('roomTypes.fields.unitLabel')}</FormLabel>
                <Select size="sm" value={form.unitLabel} onChange={set('unitLabel')}>
                  {UNIT_LABEL_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{t(`roomTypes.unitLabels.${opt}`)}</option>
                  ))}
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {t('roomTypes.fields.unitLabelHelp')}
                </Text>
              </FormControl>

              {/* Extra-guest surcharge (feature added 2026-07). Fills
                  the same use case Booking.com already enforces on the
                  OTA side: charge extra per adult / child above the
                  included allowance. Zero prices = feature off. Public
                  website + OTA reservations skip this block; it only
                  applies to staff-source direct bookings. */}
              <Box w="100%">
                <FormLabel fontSize="sm" mb={2}>{t('roomTypes.extras.title')}</FormLabel>
                <Text fontSize="xs" color="gray.500" mb={2}>
                  {t('roomTypes.extras.help')}
                </Text>
                <HStack w="100%" spacing={4}>
                  <FormControl>
                    <FormLabel fontSize="xs">{t('roomTypes.extras.includedAdults')}</FormLabel>
                    <Input size="sm" type="number" min={0} value={form.includedAdults} onChange={set('includedAdults')} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs">{t('roomTypes.extras.includedChildren')}</FormLabel>
                    <Input size="sm" type="number" min={0} value={form.includedChildren} onChange={set('includedChildren')} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs">{t('roomTypes.extras.extraAdult')}</FormLabel>
                    <Input size="sm" type="number" min={0} step={0.5} value={form.extraAdultPrice} onChange={set('extraAdultPrice')} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs">{t('roomTypes.extras.extraChild')}</FormLabel>
                    <Input size="sm" type="number" min={0} step={0.5} value={form.extraChildPrice} onChange={set('extraChildPrice')} />
                  </FormControl>
                </HStack>
              </Box>

              {/* Taxes — attach any of the hotel's tax rates (managed on
                  the Tax Rates page). The SUM of attached rates is what
                  bookings of this room type are taxed at. The flat rate
                  below only applies while nothing is attached. */}
              <Box w="100%">
                <FormLabel fontSize="sm" mb={2}>
                  {form.taxRateIds.length > 0
                    ? t('roomTypes.taxes.titleCombined', { total: selectedTaxTotal.toFixed(2) })
                    : t('roomTypes.taxes.title')}
                </FormLabel>
                {hotelTaxRates.length > 0 ? (
                  <VStack align="stretch" spacing={1} maxH="140px" overflowY="auto"
                    borderWidth="1px" borderRadius="md" p={2}>
                    {hotelTaxRates.map(r => (
                      <Checkbox key={r.id} size="sm"
                        isChecked={form.taxRateIds.includes(r.id)}
                        onChange={() => toggleTaxRate(r.id)}>
                        {r.name} — {Number(r.rate)}%{r.tax_regime === 'gst' ? ' (GST)' : ''}
                      </Checkbox>
                    ))}
                  </VStack>
                ) : (
                  <Text fontSize="xs" color="gray.500">
                    {t('roomTypes.taxes.empty')}
                  </Text>
                )}
                <HStack mt={2} spacing={3} align="center">
                  <FormControl maxW="180px">
                    <FormLabel fontSize="xs" mb={1}>{t('roomTypes.taxes.flatRate')}</FormLabel>
                    <Input size="sm" type="number" min={0} max={100} step={0.01}
                      value={form.taxRate} onChange={set('taxRate')}
                      isDisabled={form.taxRateIds.length > 0} />
                  </FormControl>
                  {form.taxRateIds.length > 0 && (
                    <Text fontSize="xs" color="gray.500" pt={4}>
                      {t('roomTypes.taxes.flatIgnored')}
                    </Text>
                  )}
                </HStack>
              </Box>

              <HStack w="100%" spacing={6}>
                <FormControl display="flex" alignItems="center">
                  <FormLabel fontSize="sm" mb={0}>{t('roomTypes.status.active')}</FormLabel>
                  <Switch isChecked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                </FormControl>
                <FormControl display="flex" alignItems="center">
                  <FormLabel fontSize="sm" mb={0}>{t('roomTypes.fields.petFriendly')}</FormLabel>
                  <Switch isChecked={form.isPetFriendly} onChange={(e) => setForm({ ...form, isPetFriendly: e.target.checked })} />
                </FormControl>
                <FormControl display="flex" alignItems="center">
                  <FormLabel fontSize="sm" mb={0}>{t('roomTypes.fields.smoking')}</FormLabel>
                  <Switch isChecked={form.isSmokingAllowed} onChange={(e) => setForm({ ...form, isSmokingAllowed: e.target.checked })} />
                </FormControl>
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>{t('common.cancel')}</Button>
            <Button colorScheme="blue" onClick={handleSave} isLoading={saving}>
              {editingId ? t('common.save') : t('common.create')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmModal
        isOpen={deleteDisclosure.isOpen} onClose={deleteDisclosure.onClose}
        onConfirm={handleDelete} title={t('roomTypes.deleteModal.title')}
        message={t('roomTypes.deleteModal.message', { name: deleteTarget?.name })}
      />

      {/* Photos Modal — manages the images guests see on the booking widget */}
      <Modal isOpen={!!photosTarget} onClose={closePhotos} size="2xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('roomTypes.photos.title', { name: photosTarget?.name })}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="xs" color="gray.500" mb={3}>{t('roomTypes.photos.hint')}</Text>
            {photosLoading ? (
              <Center py={8}><Spinner /></Center>
            ) : photos.length === 0 ? (
              <Center py={8} borderWidth="1px" borderStyle="dashed" borderRadius="md">
                <Text fontSize="sm" color="gray.500" px={6} textAlign="center">
                  {t('roomTypes.photos.empty')}
                </Text>
              </Center>
            ) : (
              <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                {photos.map((img) => (
                  <Box key={img.id} position="relative" borderWidth="1px" borderRadius="md" overflow="hidden">
                    <Image src={absUrl(img.imageUrl)} alt={img.title} objectFit="cover" w="100%" h="120px" />
                    {img.isPrimary && (
                      <Tag size="sm" colorScheme="yellow" position="absolute" top={1} left={1}>
                        <StarIcon boxSize={2.5} mr={1} />{t('roomTypes.photos.cover')}
                      </Tag>
                    )}
                    <HStack justify="space-between" p={1}>
                      {!img.isPrimary ? (
                        <Button size="xs" variant="ghost" leftIcon={<StarIcon boxSize={2.5} />}
                          onClick={() => handleSetCover(img)}>
                          {t('roomTypes.photos.setCover')}
                        </Button>
                      ) : <Box />}
                      <IconButton size="xs" icon={<DeleteIcon />} variant="ghost" colorScheme="red"
                        onClick={() => setPhotoDeleteTarget(img)} aria-label={t('common.delete')} />
                    </HStack>
                  </Box>
                ))}
              </SimpleGrid>
            )}
            <input
              ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif"
              multiple hidden onChange={handlePhotoFiles}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closePhotos}>{t('common.cancel')}</Button>
            <Button colorScheme="blue" leftIcon={<AddIcon />} isLoading={uploading}
              onClick={() => fileInputRef.current?.click()}>
              {t('roomTypes.photos.upload')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmModal
        isOpen={!!photoDeleteTarget} onClose={() => setPhotoDeleteTarget(null)}
        onConfirm={handleDeletePhoto} title={t('roomTypes.photos.deleteTitle')}
        message={t('roomTypes.photos.deleteMessage', { name: photosTarget?.name })}
      />
    </PageShell>
  );
};

export default RoomTypeManagement;
